import { CUSTOM } from '../../../constants/campaigns';
import { IChangeBucketItemPayload } from '../../../types/bucket';
import { ICampaignSummary, ICampaignSummaryItem } from '../../../types/campaigns';
import { IInventoryItem, IRequestOnDemandItem } from '../../../types/inventories';

enum actionTypes {
  Init = 'init',
  RemoveItem = 'removeItem',
  RemoveCampaign = 'removeCampaign',
  ReplaceQuantity = 'replaceQuantity',
  AddCampaign = 'addCampaign',
  AddItems = 'addItems',
  AddCampaigns = 'addCampaigns',
  ReorderCampaigns = 'reorderCampaigns',
}

export type IPayload =
  | IInventoryItem[]
  | IInventoryItem
  | ICampaignSummary
  | ICampaignSummary[]
  | IChangeBucketItemPayload
  | string;

export interface IAction {
  type: actionTypes;
  payload?: IPayload;
}

export interface IState {
  items: IInventoryItem[] | ICampaignSummaryItem[] | undefined;
  pick_campaign_ids?: string[] | null;
  pick_campaigns?: ICampaignSummary[];
  pre_created_engagement_id: string | null;
  campaign_name: string | '';
}

const initialState: IState = {
  items: [],
  pick_campaign_ids: [],
  pick_campaigns: [],
  pre_created_engagement_id: null,
  campaign_name: '',
};

const reducer = (state: IState = initialState, { type, payload }: IAction): IState => {
  switch (type) {
    case actionTypes.Init:
      return {
        ...state,
        ...(payload as Partial<IState>),
      };
    case actionTypes.RemoveItem:
      return {
        ...state,
        pre_created_engagement_id: 'custom',
        items:
          Array.isArray(state.items) && 'org_id' in state.items[0]
            ? (state.items as IInventoryItem[]).filter((item) => item.item_id !== payload)
            : state.items,
      };
    case actionTypes.RemoveCampaign: {
      const updatedCampaigns = state.pick_campaigns?.filter((c) => c.box_id !== payload) || [];
      const updatedIds = state.pick_campaign_ids?.filter((id) => id !== payload);

      // if user deletes all campaigns except 1, it becomes a single campaign
      const isLastCampaign = updatedCampaigns.length === 1;

      if (isLastCampaign) {
        const lastCampaign = updatedCampaigns[0];
        return {
          campaign_name: lastCampaign?.name || '',
          pre_created_engagement_id: lastCampaign?.box_id || '',
          items: lastCampaign?.items || [],
          pick_campaign_ids: null,
          pick_campaigns: [lastCampaign!],
        };
      }

      return {
        ...state,
        pick_campaign_ids: updatedIds,
        pick_campaigns: updatedCampaigns,
      };
    }
    case actionTypes.ReplaceQuantity:
      return {
        ...state,
        pre_created_engagement_id: CUSTOM,
        items: state.items?.map((item) => {
          if ('org_id' in item) {
            const { id, name, value } = payload as IChangeBucketItemPayload;
            return item.item_id === id ? { ...item, [name]: value } : item;
          }
          return item;
        }) as IInventoryItem[] | ICampaignSummaryItem[],
      };
    case actionTypes.AddCampaign:
      const campaign = payload as ICampaignSummary;

      return {
        ...state,
        pick_campaigns: [campaign],
        items: campaign.items,
        campaign_name: campaign.name,
        pick_campaign_ids: null,
        pre_created_engagement_id: campaign.box_id,
      };
    case actionTypes.AddItems: {
      const updateItemQuantity = (newItem: IInventoryItem, existingItems: IInventoryItem[] = []) => {
        const existingItem = existingItems.find((item) => item.item_id === newItem.item_id);
        return {
          ...newItem,
          quantity: (existingItem as unknown as IRequestOnDemandItem)?.quantity ?? 1,
        };
      };

      const updatedItems = (payload as IInventoryItem[]).map((item) =>
        updateItemQuantity(item, state.items as IInventoryItem[]),
      );

      return {
        ...state,
        items: updatedItems,
        pre_created_engagement_id: 'custom',
        pick_campaigns: undefined,
      };
    }
    case actionTypes.AddCampaigns: {
      if (Array.isArray(payload) && payload.length > 1) {
        const incoming = payload as ICampaignSummary[];
        const incomingIds = new Set(incoming.map((c) => c.box_id));
        const incomingById = new Map(incoming.map((c) => [c.box_id, c]));
        const existing = state.pick_campaigns ?? [];

        // Keep existing campaigns in their current (user-set) order, but only those still selected.
        const preserved = existing.filter((c) => incomingIds.has(c.box_id)).map((c) => incomingById.get(c.box_id)!);
        // Append newly selected campaigns to the end.
        const preservedIds = new Set(preserved.map((c) => c.box_id));
        const appended = incoming.filter((c) => !preservedIds.has(c.box_id));
        const merged = [...preserved, ...appended];

        return {
          pick_campaign_ids: merged.map((c) => c.box_id),
          pick_campaigns: merged,
          items: undefined,
          campaign_name: '',
          pre_created_engagement_id: null,
        };
      }

      // if only one campaign was chosen - reduce it to a single campaign
      const singleCampaign = Array.isArray(payload) && payload.length > 0 ? payload[0] : null;
      return {
        campaign_name: singleCampaign?.name || '',
        pre_created_engagement_id: (singleCampaign as ICampaignSummary)?.box_id || '',
        items: (singleCampaign as ICampaignSummary)?.items || [],
        pick_campaign_ids: null,
        pick_campaigns: [singleCampaign as ICampaignSummary],
      };
    }
    case actionTypes.ReorderCampaigns: {
      const reordered = (payload as ICampaignSummary[]) || [];
      return {
        ...state,
        pick_campaigns: reordered,
        pick_campaign_ids: reordered.map((c) => c.box_id),
      };
    }
    default:
      return state;
  }
};

const actions = {
  init: (payload?: Partial<IState>) => ({ type: actionTypes.Init, payload }),
  removeItem: (payload: string) => ({ type: actionTypes.RemoveItem, payload }),
  removeCampaign: (payload: string) => ({ type: actionTypes.RemoveCampaign, payload }),
  replaceQuantity: (payload: IChangeBucketItemPayload) => ({ type: actionTypes.ReplaceQuantity, payload }),
  addCampaign: (payload: ICampaignSummary) => ({ type: actionTypes.AddCampaign, payload }),
  addItems: (payload: IInventoryItem[]) => ({ type: actionTypes.AddItems, payload }),
  addCampaigns: (payload: ICampaignSummary[]) => ({ type: actionTypes.AddCampaigns, payload }),
  reorderCampaigns: (payload: ICampaignSummary[]) => ({ type: actionTypes.ReorderCampaigns, payload }),
};

export { actions, reducer, initialState };
